import React, { ComponentProps, MouseEventHandler, useState } from 'react';
import {
  IProjectColors,
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { Grid, Box } from '@mui/material';
import {
  asPercentage,
  asPercentageRounded,
  getCustomSpaceColor,
  getWorkSeatsMix,
  isSeatsAssignmentComplete,
  numberWithCommas,
  removeLineBreaks,
} from 'spw-shared/utils';
import { useDisableBulkEditButton } from 'spw-shared/hooks/use-disable-bulk-edit-button';
import { spaceFactorMap as rawSpaceFactorMap } from 'spw-programming-projects/program-summary/macro-single-page-summary/utils';
import { SmallBubbleIcon } from 'spw-components/charts/styles';
import {
  INDIVIDUAL_WORKSPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import { DecimalMenu } from 'spw-programming-projects/program-summary/components/decimal-menu';
import { colors } from 'spw-theme';
import {
  Category,
  IProjectSpaceType,
} from 'spw-redux/modules/space_types/types';
import {
  Card,
  CardTitle,
  SeatTypeMixHeader,
  SeatTypeMixColumn,
  SeatTypeMixCategoryName,
  SeatTypeMixBox,
  BoldValue,
  SeatTypeMixHeaderRow,
  AsteriskContainer,
} from 'spw-programming-projects/program-summary/macro-single-page-summary/styles';
import { PSCardGroupHeader } from 'spw-shared/globalComponents';
import { Column } from 'spw-shared/globalComponents/grid';
import { SpaceIcon } from 'spw-projects/components/space-type-icons';
import { BulkEditButton } from 'spw-projects/components/styles';
import { CategorySeatTypeMixChildRow } from './category-seat-type-mix-child';
import { EmptyListMessage } from '../../../../components/project-details-tabs/program-summary/shared/empty-list-message';
import {
  CardLegend,
  EmptyListBoxWrapper,
} from '../../../../components/project-details-tabs/program-summary/shared/styles';
import { PlainWorksheet, WorksheetTotals } from '../../../../models/Worksheet';
import WorksheetRow from '../../../../models/WorksheetRow';
import WorksheetStorageSpaceRow from '../../../../models/WorksheetStorageSpaceRow';
import { useWorksheetRowsByCategory } from './hooks/use-worksheet-rows-by-category';
import { RowContainer, SubcategoryContainer } from './style';
import BulkEditDialog from '../program-summary-tabs/bulk-edit-dialog';

const BULK_EDIT_DIALOG_SUBJECT = 'Seats';

const CategorySeatTypeMixRow = ({
  index,
  category,
  projectSpaceTypeSubcategory,
  row,
  open,
  showDecimals,
  projectColors,
  worksheet,
  detailedIndividualConfig,
  isIndividualWorkspacesCard,
  spaceTypeTotals,
  onClickSpaceIcon,
  projectSpaceTypes,
  viewOnly,
  clientFacingNamesAreVisible,
}: {
  index: number;
  category: Category;
  projectSpaceTypeSubcategory?: IProjectSpaceTypeSubcategory;
  row: WorksheetRow;
  open: boolean;
  showDecimals: boolean;
  projectColors: IProjectColors;
  worksheet: PlainWorksheet;
  detailedIndividualConfig?: boolean;
  isIndividualWorkspacesCard?: boolean;
  spaceTypeTotals: Record<string, WorksheetTotals>;
  onClickSpaceIcon: (e: Element) => void;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  viewOnly: boolean;
  clientFacingNamesAreVisible?: boolean;
}) => {
  const [bulkEditDialogProps, setBulkEditDialogProps] = useState<
    Omit<ComponentProps<typeof BulkEditDialog>, 'clientFacingNamesAreVisible'>
  >();

  const handleBulkEditDialogClose = () => {
    setBulkEditDialogProps(undefined);
  };

  const { category: subcategory } =
    projectSpaceTypeSubcategory?.defaultSpaceTypeCategory || {};
  const disableBulkEditButton = useDisableBulkEditButton({
    row,
    subject: BULK_EDIT_DIALOG_SUBJECT,
  });
  const spaceFactorMap = rawSpaceFactorMap(projectColors);
  if (row.child && !open) return null;

  if (row.child && open) {
    return (
      <CategorySeatTypeMixChildRow
        row={row}
        showDecimals={showDecimals}
        shouldShowClientFacingName={clientFacingNamesAreVisible}
        isIndividualWorkspacesCard={isIndividualWorkspacesCard}
      />
    );
  }

  const handleAvgSeatsBulkEditButtonClick = ((e) => {
    setBulkEditDialogProps({
      projectSpaceType: row.projectSpaceType,
      subject: BULK_EDIT_DIALOG_SUBJECT,
      anchorEl: e.currentTarget,
      onClose: handleBulkEditDialogClose,
    });
  }) as MouseEventHandler;

  let { quantity } = row;
  let spaceTypeSeats = row.quantity * row.seatCap;
  let seatMix = row.ratio;
  let lockersOrDrawersQuantity = 0;
  let lockersOrDrawersRatio = 0;
  let storageUnitRatio = 0;
  if (detailedIndividualConfig || row.isStorageSupportSpace) {
    const parentTotals = worksheet.spaceTypesTotals[row.uniqueKey];
    spaceTypeSeats = parentTotals?.seats;
    quantity = parentTotals?.spacesQuantity;
    seatMix = parentTotals?.seatMix ?? 0;
    lockersOrDrawersQuantity = parentTotals?.lockersOrDrawersQuantity;
    const { dividendForRatio } = row as WorksheetStorageSpaceRow;
    lockersOrDrawersRatio = lockersOrDrawersQuantity
      ? dividendForRatio / lockersOrDrawersQuantity
      : 0;
    storageUnitRatio = quantity ? dividendForRatio / quantity : 0;
  }

  const seatMixPercent = seatMix / 100;
  let seatMixPercentValue = '0%';
  if (!Number.isNaN(seatMixPercent)) {
    seatMixPercentValue = showDecimals
      ? asPercentage(seatMixPercent)
      : asPercentageRounded(seatMixPercent, false);
  }

  if (row.spaceType) {
    const spaceTypeData = spaceFactorMap[row.spaceType.spaceType];
    const rowIcon =
      projectSpaceTypes[row.projectSpaceType?.id]?.icon || row.spaceType.icon;
    let color = spaceTypeData?.color || projectColors[category];

    const wasSpaceTypeReassigned =
      spaceTypeData && row.category !== spaceTypeData.defaultCategory;
    if (wasSpaceTypeReassigned) {
      color = projectColors[row.category];
    }

    if (!spaceTypeData) {
      color = getCustomSpaceColor(
        subcategory || category,
        row.spaceType.spaceType,
        projectColors
      );
    }

    const getThirdColumnData = () => {
      if (
        isIndividualWorkspacesCard &&
        projectSpaceTypeSubcategory?.includeInTotalWorkSeats
      )
        return {
          label: 'Seat Mix %',
          value: seatMixPercentValue,
        };
      const ratio = spaceTypeTotals[row.uniqueKey]?.ratio || 0;
      if (isIndividualWorkspacesCard)
        return {
          label: 'Target Ratio',
          value: `1: ${numberWithCommas(ratio, showDecimals, true)}`,
        };

      return {
        label: 'Avg Seats',
        value: numberWithCommas(Math.round(row.seatCap), showDecimals, true),
      };
    };

    const thirdColumnData = getThirdColumnData();
    const testingPrefixForRow = `programming_ps_seat_type_mix_row_${row?.spaceType?.id}`;
    const projectSpaceTypeCategory = row.projectSpaceType?.category;

    const clientFacingName = row.projectSpaceType?.clientFacingName;

    const getSpaceTypeName = () => {
      if (clientFacingName && clientFacingNamesAreVisible)
        return clientFacingName;
      return row.spaceType.name.toUpperCase();
    };

    let thirdColumn = (
      <>
        <Box>
          {row.isStorageSupportSpace ? (
            <BoldValue data-testid={`${testingPrefixForRow}_unitCount`}>
              {numberWithCommas(quantity, showDecimals, true)}
              <AsteriskContainer>*</AsteriskContainer>
            </BoldValue>
          ) : (
            <BoldValue data-testid={`${testingPrefixForRow}_seatMix`}>
              {thirdColumnData.value}
            </BoldValue>
          )}
        </Box>
        {row.isStorageSupportSpace ? (
          <Box style={{ maxWidth: 96, textAlign: 'right' }}>
            {(row as WorksheetStorageSpaceRow).storageUnitQuantityLabel}
          </Box>
        ) : (
          <Box>{thirdColumnData.label}</Box>
        )}
      </>
    );

    if (!isIndividualWorkspacesCard) {
      thirdColumn = (
        <>
          <BulkEditButton
            $disableHoverState={disableBulkEditButton}
            className={bulkEditDialogProps ? 'selected' : ''}
            onClick={
              !disableBulkEditButton
                ? handleAvgSeatsBulkEditButtonClick
                : undefined
            }
          >
            {thirdColumn}
          </BulkEditButton>
          {bulkEditDialogProps && (
            <BulkEditDialog
              {...bulkEditDialogProps}
              clientFacingNamesAreVisible={clientFacingNamesAreVisible}
            />
          )}
        </>
      );
    }

    return (
      <RowContainer
        data-testid={`programming_ps_seat_type_mix_row_${row?.spaceType?.name}_container`}
        className={
          open && index !== 0 && category === INDIVIDUAL_WORKSPACES_CATEGORY
            ? 'show-border-top'
            : ''
        }
      >
        {row.isStorageSupportSpace ? (
          <PSCardGroupHeader>
            <Box
              display="flex"
              alignItems="center"
              data-testid={`${testingPrefixForRow}_spaceTypeName`}
            >
              <span>{row.spaceType.name.toUpperCase()}</span>
            </Box>
          </PSCardGroupHeader>
        ) : null}
        <Grid container style={{ padding: '0' }} key="top">
          <SeatTypeMixCategoryName item xs={12} className="borderless">
            <div
              data-cy={`programming_ps_seat_type_mix_row_${row?.spaceType?.name}`}
              data-testid={`${testingPrefixForRow}_spaceTypeName`}
            >
              <pre>{removeLineBreaks(getSpaceTypeName())}</pre>
            </div>
          </SeatTypeMixCategoryName>
        </Grid>
        <Grid container key="second">
          <SeatTypeMixColumn
            item
            xs={5}
            style={{
              paddingBottom: 8,
            }}
          >
            <Grid container>
              <SeatTypeMixColumn item xs={6}>
                <Box display="flex" flexDirection="row" flexWrap="nowrap">
                  <Box>
                    <SpaceIcon
                      disabled={viewOnly}
                      projectSpaceTypeCategoryId={projectSpaceTypeCategory.id}
                      topLevelProjectSpaceTypeCategoryId={
                        (projectSpaceTypeCategory as IProjectSpaceTypeSubcategory)
                          ?.parentId || projectSpaceTypeCategory.id
                      }
                      projectSpaceTypeId={row.projectSpaceType?.id}
                      onClick={onClickSpaceIcon}
                      iconName={rowIcon}
                      defaultIconName={row.spaceType?.icon}
                      name={
                        spaceTypeData
                          ? `${spaceTypeData.title}`
                          : `${row.spaceType.name}`
                      }
                    />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <SmallBubbleIcon color={color as string} />
                  </Box>
                </Box>
              </SeatTypeMixColumn>
              <SeatTypeMixColumn
                item
                xs={6}
                style={{
                  marginTop: row.isStorageSupportSpace ? -12 : 0,
                }}
              >
                <SeatTypeMixBox
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  flexDirection="column"
                >
                  <Box>
                    {row.isStorageSupportSpace ? (
                      <BoldValue
                        data-testid={`${testingPrefixForRow}_quantity`}
                      >
                        {numberWithCommas(
                          lockersOrDrawersQuantity,
                          showDecimals,
                          true
                        )}
                        <AsteriskContainer>*</AsteriskContainer>
                      </BoldValue>
                    ) : (
                      <BoldValue
                        data-testid={`${testingPrefixForRow}_quantity`}
                      >
                        {numberWithCommas(quantity, showDecimals, true)}
                      </BoldValue>
                    )}
                  </Box>
                  <Box style={{ textAlign: 'right' }}>
                    {row.isStorageSupportSpace ? (
                      <span>
                        Quantity of <br />
                        {(row as WorksheetStorageSpaceRow).storageTypeLabel}
                      </span>
                    ) : (
                      'Quantity'
                    )}
                  </Box>
                </SeatTypeMixBox>
              </SeatTypeMixColumn>
            </Grid>
          </SeatTypeMixColumn>
          <Grid item xs={7}>
            <Grid container>
              <SeatTypeMixColumn
                item
                xs={!isIndividualWorkspacesCard ? 4 : 6}
                style={
                  !isIndividualWorkspacesCard
                    ? {
                        marginTop: row.isStorageSupportSpace ? -12 : 4,
                      }
                    : {}
                }
              >
                <SeatTypeMixBox
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  flexDirection="column"
                >
                  <Box>
                    {row.isStorageSupportSpace ? (
                      <BoldValue data-testid={`${testingPrefixForRow}_ratio`}>
                        1:{' '}
                        {numberWithCommas(
                          lockersOrDrawersRatio,
                          showDecimals,
                          true
                        )}
                      </BoldValue>
                    ) : (
                      <BoldValue data-testid={`${testingPrefixForRow}_seats`}>
                        {numberWithCommas(spaceTypeSeats, showDecimals, true)}
                        {!row.isIncludedInTotalWorkSeats && (
                          <AsteriskContainer>*</AsteriskContainer>
                        )}
                      </BoldValue>
                    )}
                  </Box>
                  <Box
                    style={
                      !isIndividualWorkspacesCard
                        ? {
                            marginBottom: '4px',
                            textAlign: 'right',
                            maxWidth: 90,
                          }
                        : undefined
                    }
                  >
                    {row.isStorageSupportSpace
                      ? (row as WorksheetStorageSpaceRow)
                          .lockersOrDrawersRatioLabel
                      : 'Seats'}
                  </Box>
                </SeatTypeMixBox>
              </SeatTypeMixColumn>
              <SeatTypeMixColumn
                item
                xs={!isIndividualWorkspacesCard ? 5 : 6}
                className={
                  !isIndividualWorkspacesCard ? 'vertical-stretch' : ''
                }
                style={
                  !isIndividualWorkspacesCard
                    ? {
                        paddingBottom: '8px',
                        paddingTop: 2,
                        borderRight: row.isStorageSupportSpace
                          ? 'none'
                          : undefined,
                      }
                    : {}
                }
              >
                <SeatTypeMixBox
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  flexDirection="column"
                >
                  {thirdColumn}
                </SeatTypeMixBox>
              </SeatTypeMixColumn>
              {!isIndividualWorkspacesCard && (
                <SeatTypeMixColumn
                  item
                  xs={3}
                  style={{
                    paddingTop: '4px',
                    marginTop: row.isStorageSupportSpace ? -16 : 0,
                  }}
                >
                  <SeatTypeMixBox
                    display="flex"
                    alignItems="flex-end"
                    justifyContent="flex-end"
                    flexDirection="column"
                  >
                    <Box>
                      {row.isStorageSupportSpace ? (
                        <BoldValue
                          data-testid={`${testingPrefixForRow}_unitRatio`}
                        >
                          1:{' '}
                          {numberWithCommas(
                            storageUnitRatio,
                            showDecimals,
                            true
                          )}
                        </BoldValue>
                      ) : (
                        <BoldValue data-testid={`${testingPrefixForRow}_ratio`}>
                          1: {numberWithCommas(row.ratio, showDecimals, true)}
                        </BoldValue>
                      )}
                    </Box>
                    {row.isStorageSupportSpace ? (
                      <Box
                        style={{
                          marginBottom: '4px',
                          textAlign: 'right',
                          maxWidth: 85,
                        }}
                      >
                        {
                          (row as WorksheetStorageSpaceRow)
                            .storageUnitRatioLabel
                        }
                      </Box>
                    ) : (
                      <Box style={{ marginBottom: '4px' }}>Ratio</Box>
                    )}
                  </SeatTypeMixBox>
                </SeatTypeMixColumn>
              )}
            </Grid>
          </Grid>
        </Grid>
      </RowContainer>
    );
  }

  return null;
};

const CategorySeatTypeMix = ({
  projectSpaceTypeCategory,
  worksheet,
  open = false,
  projectColors,
  detailedIndividualConfig,
  isRenderingForExport,
  onClickSpaceIcon,
  projectSpaceTypes,
  viewOnly,
  clientFacingNamesAreVisible,
}: {
  projectSpaceTypeCategory: IProjectSpaceTypeCategory;
  worksheet: PlainWorksheet;
  open?: boolean;
  projectColors: IProjectColors;
  detailedIndividualConfig?: boolean;
  isRenderingForExport?: boolean;
  onClickSpaceIcon?: (e: Element) => void;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  viewOnly: boolean;
  clientFacingNamesAreVisible?: boolean;
}) => {
  if (!worksheet || !projectSpaceTypeCategory) return null;
  const { category } = projectSpaceTypeCategory.defaultSpaceTypeCategory;
  const { name: categoryName } = projectSpaceTypeCategory;
  const [showDecimals, setShowDecimals] = useState(false);

  const { rowsBySubcategory } = worksheet;
  const {
    categoryRows,
    hasRowsBelongingToASubcategory,
  } = useWorksheetRowsByCategory(projectSpaceTypeCategory, worksheet, open);

  const totals = worksheet?.categoriesTotals?.[category] || {
    seats: 0,
    spacesQuantity: 0,
    USF: 0,
  };

  const totalWorkSeats = worksheet.scenario?.workSeats || 0;

  const worksheetWorkSeats = worksheet.workspacesSeats || 0;

  const isSeatsAssignmentCompleted = isSeatsAssignmentComplete(
    totalWorkSeats,
    worksheetWorkSeats
  );

  const scenarioSeatMix =
    getWorkSeatsMix(totalWorkSeats, worksheetWorkSeats, {
      showDecimals,
      showNoData: false,
    }) || '0%';

  const isIndividualWorkspacesCard =
    category === INDIVIDUAL_WORKSPACES_CATEGORY;

  const worksheetHasIWRows = Boolean(
    worksheet.rowsByCategory[INDIVIDUAL_WORKSPACES_CATEGORY].length
  );

  const shouldShowNotIncludedInTotal =
    (worksheet.hasWorkspacesNotIncludedInTotalSeats &&
      isIndividualWorkspacesCard &&
      worksheetHasIWRows) ||
    (category === SUPPORT_SPACES_CATEGORY && worksheet.hasStorageRows);

  const testingPrefix = `programming_ps_seat_type_mix_${category}`;

  return (
    <Card
      style={{
        boxSizing: 'border-box',
        height: '100%',
      }}
    >
      <Box display="flex" flexDirection="column" style={{ height: '100%' }}>
        <CardTitle key="title" data-testid={`${testingPrefix}_title`}>
          <div style={{ width: 'calc(100% - 40px)' }}>
            {categoryName.toUpperCase()}
            {isIndividualWorkspacesCard ? ` SEAT TYPE MIX` : ` RATIOS`}
          </div>
          {!isRenderingForExport && (
            <DecimalMenu
              onShowDecimals={(showDecimalsVal: boolean) =>
                setShowDecimals(showDecimalsVal)
              }
            />
          )}
        </CardTitle>
        <Grid container key="grid">
          <SeatTypeMixHeaderRow item xs={5} key="first">
            <SeatTypeMixHeader>
              <SeatTypeMixColumn item xs={6} style={{ paddingTop: 16 }}>
                Totals
              </SeatTypeMixColumn>
              <SeatTypeMixColumn item xs={6}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                >
                  <div>
                    <BoldValue
                      style={
                        isSeatsAssignmentCompleted ||
                        !isIndividualWorkspacesCard
                          ? undefined
                          : { color: colors.red }
                      }
                      data-testid={`${testingPrefix}_totalQuantity`}
                    >
                      {numberWithCommas(
                        totals?.spacesQuantity,
                        showDecimals,
                        true
                      )}
                    </BoldValue>
                  </div>
                  <div>Quantity</div>
                </div>
              </SeatTypeMixColumn>
            </SeatTypeMixHeader>
          </SeatTypeMixHeaderRow>

          <SeatTypeMixHeaderRow item xs={7} key="second">
            <SeatTypeMixHeader style={{ paddingBottom: 0 }} container>
              <SeatTypeMixColumn item xs={isIndividualWorkspacesCard ? 6 : 4}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                >
                  <div style={{ textAlign: 'right' }}>
                    <div>
                      <BoldValue
                        style={
                          isSeatsAssignmentCompleted ||
                          !isIndividualWorkspacesCard
                            ? undefined
                            : { color: colors.red }
                        }
                        data-testid={`${testingPrefix}_totalSeats`}
                      >
                        {numberWithCommas(totals.seats, showDecimals, true)}
                      </BoldValue>
                    </div>
                    <Box>Seats</Box>
                  </div>
                </div>
              </SeatTypeMixColumn>
              <SeatTypeMixColumn
                item
                xs={!isIndividualWorkspacesCard ? 5 : 6}
                className={!isIndividualWorkspacesCard ? 'border-right' : ''}
              >
                {isIndividualWorkspacesCard && (
                  <Grid
                    container
                    justifyContent="flex-end"
                    alignItems="flex-end"
                    direction="column"
                  >
                    <BoldValue
                      style={
                        isSeatsAssignmentCompleted ||
                        !isIndividualWorkspacesCard
                          ? undefined
                          : { color: colors.red }
                      }
                      data-testid={`${testingPrefix}_seatMixHeader`}
                    >
                      {isSeatsAssignmentCompleted || !isIndividualWorkspacesCard
                        ? ''
                        : scenarioSeatMix}
                    </BoldValue>
                    <div>Seat Mix %</div>
                  </Grid>
                )}
              </SeatTypeMixColumn>
              {!isIndividualWorkspacesCard && (
                <SeatTypeMixColumn item xs={3}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      alignItems: 'flex-end',
                    }}
                  >
                    <div style={{ textAlign: 'right' }}>
                      <div>
                        <BoldValue
                          style={
                            isSeatsAssignmentCompleted ||
                            !isIndividualWorkspacesCard
                              ? undefined
                              : { color: colors.red }
                          }
                          data-testid={`${testingPrefix}_totalSeats`}
                        >
                          1:{' '}
                          {numberWithCommas(
                            totalWorkSeats / totals?.spacesQuantity,
                            showDecimals,
                            true
                          )}
                        </BoldValue>
                      </div>
                      <Box>Ratio</Box>
                    </div>
                  </div>
                </SeatTypeMixColumn>
              )}
            </SeatTypeMixHeader>
          </SeatTypeMixHeaderRow>
        </Grid>

        <PSCardGroupHeader color={colors.veryLightGray}>
          <Box
            display="flex"
            alignItems="center"
            data-testid={`${testingPrefix}_categoryName`}
          >
            <span>{categoryName}</span>
          </Box>
        </PSCardGroupHeader>

        <Column
          style={
            !hasRowsBelongingToASubcategory
              ? { flexGrow: 1, justifyContent: 'center' }
              : {}
          }
        >
          {categoryRows.length
            ? categoryRows.map((row: WorksheetRow, index: number) => {
                return (
                  <CategorySeatTypeMixRow
                    viewOnly={viewOnly}
                    projectSpaceTypes={projectSpaceTypes}
                    onClickSpaceIcon={onClickSpaceIcon}
                    key={`${row.id}-${row.spaceType.id}`}
                    index={index}
                    spaceTypeTotals={worksheet.spaceTypesTotals}
                    category={category as Category}
                    row={row}
                    open={open}
                    showDecimals={showDecimals}
                    projectColors={projectColors}
                    worksheet={worksheet}
                    detailedIndividualConfig={detailedIndividualConfig}
                    isIndividualWorkspacesCard={isIndividualWorkspacesCard}
                    clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                  />
                );
              })
            : !hasRowsBelongingToASubcategory && (
                <EmptyListBoxWrapper>
                  <EmptyListMessage />
                </EmptyListBoxWrapper>
              )}
        </Column>

        {projectSpaceTypeCategory.subcategories?.length > 0 && (
          <SubcategoryContainer>
            {projectSpaceTypeCategory.subcategories.map(
              (subcategory: IProjectSpaceTypeSubcategory) => {
                const {
                  category: defaultSubcategory,
                } = subcategory.defaultSpaceTypeCategory;

                return (
                  rowsBySubcategory[defaultSubcategory]?.length > 0 && (
                    <div key={`${subcategory.id}`}>
                      <PSCardGroupHeader>
                        <Box display="flex" alignItems="center">
                          <span>{subcategory.name}</span>
                        </Box>
                      </PSCardGroupHeader>
                      {rowsBySubcategory[defaultSubcategory].map(
                        (row: WorksheetRow, index: number) => {
                          return (
                            <CategorySeatTypeMixRow
                              viewOnly={viewOnly}
                              projectSpaceTypes={projectSpaceTypes}
                              onClickSpaceIcon={onClickSpaceIcon}
                              index={index}
                              key={`${row.id}-${row.spaceType.id}`}
                              category={category as Category}
                              projectSpaceTypeSubcategory={subcategory}
                              spaceTypeTotals={worksheet.spaceTypesTotals}
                              row={row}
                              open={open}
                              showDecimals={showDecimals}
                              projectColors={projectColors}
                              worksheet={worksheet}
                              detailedIndividualConfig={
                                detailedIndividualConfig
                              }
                              isIndividualWorkspacesCard={
                                isIndividualWorkspacesCard
                              }
                              clientFacingNamesAreVisible={
                                clientFacingNamesAreVisible
                              }
                            />
                          );
                        }
                      )}
                    </div>
                  )
                );
              }
            )}
          </SubcategoryContainer>
        )}
        {shouldShowNotIncludedInTotal && (
          <CardLegend>
            * Not included in Total{' '}
            {isIndividualWorkspacesCard ? 'Work Seats' : 'Quantity'}
          </CardLegend>
        )}
      </Box>
    </Card>
  );
};

const MemoizedCategorySeatTypeMix = React.memo(
  CategorySeatTypeMix,
  (prevProps, nextProps) => {
    const {
      category,
    } = prevProps.projectSpaceTypeCategory.defaultSpaceTypeCategory;

    const prevSpacesQuantity =
      prevProps.worksheet?.categoriesTotals?.[category]?.spacesQuantity;
    const nextSpacesQuantity =
      nextProps.worksheet?.categoriesTotals?.[category]?.spacesQuantity;

    const prevProjectSpaceTypes = prevProps.projectSpaceTypes;
    const nextProjectSpaceTypes = nextProps.projectSpaceTypes;

    const someAvgSeatCountChanged = nextProps.worksheet.rows.some(
      (newRow, i) => newRow?.seatCap !== prevProps.worksheet?.rows?.[i]?.seatCap
    );

    return (
      prevSpacesQuantity === nextSpacesQuantity &&
      prevProps.open === nextProps.open &&
      prevProjectSpaceTypes === nextProjectSpaceTypes &&
      prevProps.clientFacingNamesAreVisible ===
        nextProps.clientFacingNamesAreVisible &&
      !someAvgSeatCountChanged
    );
  }
);

MemoizedCategorySeatTypeMix.whyDidYouRender = true;

export { MemoizedCategorySeatTypeMix as CategorySeatTypeMix };
